import { useMemo, useRef, useState } from 'react';

import { TrashIcon } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { FlexibleDiv } from '@components/formStyles';
import { useTourDetailVariationContext } from '@contexts/tour-detail/TourDetail';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import { SeturLoyaltyInfoViewModel, TourVariationViewModel } from '@api/v3';

import { Props } from './props';
import {
  Wrapper,
  RoomHeader,
  RoomContent,
  DeleteRoomButton,
  Label,
} from './styled';
import { Guest } from '../TourRoomGuest/props';
import TourRoomGuest from '../TourRoomGuest';
import TourSelectionWarnings from '../TourSelectionWarnings';
import { useGetTourVariations } from './hooks';
import { StyledInfoModal, Text } from '../styled';
import {
  getGuestsInfoModalText,
  validateSingleTourRoomChildrenAges,
} from '../TourSelectionCard/helpers';
import VariationPriceList, {
  ResetSelectionFNRef,
} from '../VariationPriceList/VariationPriceList';

const TourSelectionRoom = ({
  packageId,
  personQuota,
  removeRoom,
  roomId,
  roomIndex,
  tourId,
  packageDetailId,
  price3,
  companyCommissionAmount,
  selectedVariationTitle,
  day,
  nightCount,
  type,
}: Props) => {
  const [guests, setGuests] = useState<Guest>({ id: 0, adultNumber: 1 });
  const resetVariationSelectionFnRef = useRef() as ResetSelectionFNRef;
  const [guestInfoModalText, setGuestInfoModalText] = useState('');

  const { addPackageRoomVariation, resetPackageRoomVariations, tourPackages } =
    useTourDetailVariationContext();

  // ===================================
  //  Get Detail Variations
  // ===================================
  const {
    tourDetailVariations,
    showEmptyVariationGuestsNote,
    showGuestsWarning,
    showGuestsWarningPrice,
  } = useGetTourVariations(
    tourId ?? 0,
    packageDetailId ?? 0,
    guests,
    type,
    personQuota,
    price3
  );

  useSkipEffectOnMount(() => {
    if (tourDetailVariations?.length === 0) return;

    const seturPointsVarations: SeturLoyaltyInfoViewModel[] = [];
    tourDetailVariations?.forEach((item: TourVariationViewModel) => {
      if (item.seturLoyaltyInfo) {
        seturPointsVarations.push(item.seturLoyaltyInfo);
      }
    });
  }, [tourDetailVariations]);

  // ===================================
  //  Handle selected variation
  // ===================================
  const onVariationSelected = (variation: TourVariationViewModel) => {
    if (!validateSingleTourRoomChildrenAges(guests)) return;

    const data = {
      id: packageId,
      title: selectedVariationTitle,
      price: 0,
      currency: '',
      guests: [guests] || [],
      day: day,
      nightCount: nightCount,
      packageDetailId: packageDetailId,
      companyCommissionAmount: companyCommissionAmount,
      variationId: variation.variationId,
      roomId,
      variationIndex: tourDetailVariations?.indexOf(variation),
    };

    addPackageRoomVariation(packageDetailId ?? -1, roomId, {
      ...variation,
      ...data,
    });
  };

  // ======================================
  //  Reset room variation when guest changes
  // ======================================
  useSkipEffectOnMount(() => {
    resetPackageRoomVariations(packageDetailId ?? -1, roomId);
  }, [guests]);

  // ======================================
  //  Get the selected variation index
  // ======================================
  const selectedVariationIndex = useMemo(() => {
    const currentPackage = tourPackages?.find(
      (packageItem) => packageItem.packageDetailId === packageDetailId
    );
    const currentRoom = currentPackage?.rooms?.find(
      (room) => room.roomId === roomId
    );

    return currentRoom?.selectedVariationIndex ?? -1;
  }, [tourPackages]);

  useSkipEffectOnMount(() => {
    setGuestInfoModalText(getGuestsInfoModalText(guests));
  }, [guests.adultNumber, guests?.childList?.length]);

  // ======================================
  //  Check if room selected
  // ======================================
  const isSelected = useMemo(() => {
    const currentPackage = tourPackages?.find(
      (packageItem) => packageItem.packageDetailId === packageDetailId
    );
    const currentRoom = currentPackage?.rooms?.find(
      (room) => room.roomId === roomId
    );

    return !!currentRoom?.selectedVariationItems?.find(
      (selected) => selected.packageDetailId === packageDetailId
    );
  }, [tourPackages]);

  // ======================================
  //  Reset radio group selection
  // ======================================
  useSkipEffectOnMount(() => {
    if (!isSelected) {
      resetVariationSelectionFnRef.current?.();
    }
  }, [isSelected]);

  // ===============================
  // Conditional Render variables
  // ===============================
  const hasWarning =
    showGuestsWarning || showGuestsWarningPrice || showEmptyVariationGuestsNote;

  return (
    <Wrapper>
      <RoomHeader>
        <Label size={18}>{roomIndex + 1} . Oda</Label>
        <DeleteRoomButton onClick={() => removeRoom(roomId, roomIndex)}>
          <Label size={16}>Sil</Label>
          <CustomIcon
            paletteColor='persianRed'
            icon={TrashIcon}
            fontSize={20}
          />
        </DeleteRoomButton>
      </RoomHeader>
      <RoomContent>
        <FlexibleDiv bottom={20}>
          <TourRoomGuest
            guests={guests}
            setGuests={setGuests}
            validChildrenAges={validateSingleTourRoomChildrenAges(guests)}
          />
        </FlexibleDiv>

        {hasWarning ? (
          <TourSelectionWarnings
            showGuestsWarning={showGuestsWarning}
            showGuestsWarningPrice={showGuestsWarningPrice}
            showEmptyVariationGuestsNote={showEmptyVariationGuestsNote}
            personQuota={personQuota}
          />
        ) : (
          <VariationPriceList
            tourCard={true}
            title='Aşağıdaki opsiyonlardan birini seçerek ilerleyiniz.'
            variations={tourDetailVariations}
            onVariationSelected={onVariationSelected}
            selectedIndex={selectedVariationIndex}
            resetRef={resetVariationSelectionFnRef}
            disabled={validateSingleTourRoomChildrenAges(guests) ? false : true}
          />
        )}
      </RoomContent>
      {guestInfoModalText ? (
        <StyledInfoModal
          title={
            <Text size={25} bold>
              Uyarı
            </Text>
          }
          content={<span>{guestInfoModalText}</span>}
          onCancel={() => setGuestInfoModalText('')}
        />
      ) : null}
    </Wrapper>
  );
};

export default TourSelectionRoom;
